'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Menu } from 'lucide-react';
import { SITE_CONFIG, NAV_LINKS } from '@/data/site-constants';
import { LanguageSwitcher } from '@/components/layout/language-switcher';
import { MobileNav } from './mobile-nav';
import { BrandWordmark } from './brand-wordmark';

const MOBILE_NAV_ID = 'mobile-nav-drawer';

export function SiteHeader() {
  const pathname = usePathname();
  const t = useTranslations();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium"
      >
        {t('public.header.skipToContent')}
      </a>

      {/* Top bar — hidden on mobile */}
      <div className="hidden bg-eifo-primary text-white/80 md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs">
          <span>{SITE_CONFIG.address}</span>
          <div className="flex items-center gap-4">
            {SITE_CONFIG.phones.map((p) => (
              <a key={p.number} href={`tel:${p.number.replace(/\s/g, '')}`} className="hover:text-white">
                {p.label}: {p.number}
              </a>
            ))}
            <a href={`mailto:${SITE_CONFIG.email}`} className="hover:text-white">
              {SITE_CONFIG.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav bar */}
      <header className="sticky top-0 z-50 border-b bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          {/* Logo + name */}
          <Link href="/" className="flex items-center gap-2">
            <Image src="/eifo-logo.png" alt={t('public.header.homeLogoAlt')} width={40} height={40} />
            <BrandWordmark className="text-xl" />
          </Link>

          {/* Desktop nav */}
          <nav aria-label={t('public.header.mainNavigation')} className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((link) => {
              const isActive =
                link.href === '/'
                  ? pathname === '/'
                  : pathname === link.href || pathname.startsWith(link.href + '/');
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-eifo-orange/10 text-eifo-orange'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-eifo-primary'
                  }`}
                >
                  {t(link.labelKey)}
                </Link>
              );
            })}
          </nav>

          {/* Desktop auth buttons */}
          <div className="hidden items-center gap-2 md:flex">
            <LanguageSwitcher />
            <Link
              href="/login"
              className="rounded-md px-4 py-2 text-sm font-medium text-eifo-primary hover:bg-gray-100"
            >
              {t('public.header.logIn')}
            </Link>
            <Link
              href="/register"
              className="rounded-md bg-eifo-orange px-4 py-2 text-sm font-medium text-white hover:bg-eifo-orange/90"
            >
              {t('public.header.register')}
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="rounded-md p-2 text-gray-700 hover:bg-gray-100 md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-expanded={mobileOpen}
            aria-controls={MOBILE_NAV_ID}
            aria-label={t('public.header.openNavigationMenu')}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <MobileNav
        id={MOBILE_NAV_ID}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        links={NAV_LINKS}
      />
    </>
  );
}
